import {
  buildStandardHeaders,
  OtsAsyncThunkConfigExtra,
  removeUndefined,
  TransportCategory
} from "@ots-frontend/ots-data";
import {useCallback, useEffect, useState} from "react";
import {notifications} from "@mantine/notifications";
import {useTranslation} from "react-i18next";
import {useApp} from "../providers/RegionProvider/RegionProvider"
import {BaseQueryApi} from "@reduxjs/toolkit/query";

export type UseDownloadContainerStatusReportProps = {
  baseQueryApi: BaseQueryApi
}

export type UseDownloadContainerStatusReportResult = {
  downloadContainerStatusReport: (category: TransportCategory, scope?: ContainerStatusReportScope) => void
}

export type ContainerStatusReportScope = {
  assignedOnly?: boolean
  siteCompanyId?: string
  containerIds?: string[]
}

export const useDownloadContainerStatusReport = (props: UseDownloadContainerStatusReportProps): UseDownloadContainerStatusReportResult => {
  const {baseQueryApi} = props

  const {regionId} = useApp()
  const {t} = useTranslation(["common"])

  // state
  const [_loading, _setLoading] = useState<boolean>(false)

  useEffect(() => {
    if (_loading) {
      notifications.show({
        id: "useDownloadContainerStatusReport_updating",
        title: t("common.status.downloading", "Downloading") + " " + t("common.shared.report", "Report"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useDownloadContainerStatusReport_updating")
    }
  }, [_loading, t]);

  const _handleError = useCallback((rejected: {data?: {detail?: string}}) => {
    notifications.show({
      color: "red",
      title: t("common.error", "Error"),
      message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"}),
    })
  }, [t])

  const _downloadContainerStatusReport = useCallback((category: TransportCategory, scope?: ContainerStatusReportScope) => {

    if (!regionId) {
      notifications.show({
        color: "red",
        title: t("common.error", "Error"),
        message: t<string>("common.error", "Error"),
      })
      return
    }

    _setLoading(true)

    const headers = buildStandardHeaders(baseQueryApi)
    headers["Accept"] = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    headers["Content-Type"] = 'application/json'

    const request: RequestInit = {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(
        removeUndefined({
          regionIds: [regionId],
          category: category.code,
          assignedOnly: scope?.assignedOnly,
          siteCompanyIds: scope?.siteCompanyId ? [scope.siteCompanyId] : undefined,
          containerIds: scope?.containerIds,
        })
      ),
    }

    const baseUrl = (baseQueryApi.extra as OtsAsyncThunkConfigExtra).baseUrl;
    const url = `${baseUrl}/containerlevelhistory/container-status-report`;

    window.fetch(url, request)
      .then((response) => {
        if (!response.ok) {
          return response.json().then((errorDetail) => {
            return Promise.reject(errorDetail);
          });
        }
        return response.blob();
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${new Date().getTime()}.xlsx`
        a.click();

        notifications.show({
          color: "green",
          title: t("common.shared.report", "Report") + " " + t("common.status.downloaded", "Downloaded"),
          message: ""
        });
      })
      .catch((error) => {
        _handleError(error);
      })
      .finally(() => {
        _setLoading(false);
      });
  }, [regionId, _setLoading, baseQueryApi, t, _handleError])

  return {
    downloadContainerStatusReport: _downloadContainerStatusReport,
  }
}
